import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Form, Table } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { getInvoicesAPI, markInvoicePaidAPI, sendPaymentReminderAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import DrawerPanel from '../../../Components/shared/DrawerPanel';
import { platformUrl } from '../../../utils/mhNav';
import styles from './Invoices.module.scss';

function statusBadge(s) {
  const x = String(s).toLowerCase();
  if (x === 'paid') return { bg: 'success', label: s };
  if (x === 'failed') return { bg: 'danger', label: s };
  if (x === 'overdue') return { bg: 'warning', text: 'dark', label: s };
  return { bg: 'secondary', label: s };
}

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');
  const [drawer, setDrawer] = useState(null);
  const [notes, setNotes] = useState('');
  const [paidDate, setPaidDate] = useState(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getInvoicesAPI({
        status,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        tenantSearch,
      });
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [status, dateFrom, dateTo, tenantSearch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (drawer) setNotes(drawer.paymentNotes || '');
  }, [drawer]);

  const columns = useMemo(
    () => [
      { name: 'Invoice #', selector: (r) => r.invoiceNumber, sortable: true },
      { name: 'Tenant', selector: (r) => r.companyName, sortable: true, grow: 1 },
      { name: 'Amount', selector: (r) => `$${r.amountUsd.toLocaleString()}`, sortable: true },
      {
        name: 'Issue',
        selector: (r) => new Date(r.issuedAt).toLocaleDateString(),
        sortable: true,
      },
      {
        name: 'Due',
        selector: (r) => new Date(r.dueAt).toLocaleDateString(),
        sortable: true,
      },
      {
        name: 'Status',
        cell: (r) => {
          const b = statusBadge(r.status);
          return (
            <Badge bg={b.bg} text={b.text}>
              {b.label}
            </Badge>
          );
        },
      },
      {
        name: 'Actions',
        ignoreRowClick: true,
        cell: (r) => (
          <div className="d-flex flex-wrap gap-1">
            <Button
              size="sm"
              variant="outline-success"
              onClick={async () => {
                try {
                  await markInvoicePaidAPI(r.id, new Date().toISOString());
                  toast.success('Marked paid');
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Mark paid
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={async () => {
                try {
                  await sendPaymentReminderAPI(r.id);
                  toast.success('Reminder sent');
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Remind
            </Button>
            <Button size="sm" variant="link" className="p-0" onClick={() => toast.success('PDF download (simulated)')}>
              PDF
            </Button>
          </div>
        ),
      },
    ],
    [load]
  );

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader title="Invoices" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Billing', path: platformUrl('billing') }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Invoices" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Billing', path: platformUrl('billing') }]} />

      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div>
          <Form.Label className="small text-muted">Status</Form.Label>
          <Form.Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 140 }}>
            {['All', 'Paid', 'Pending', 'Overdue', 'Failed'].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Form.Select>
        </div>
        <div>
          <Form.Label className="small text-muted">From</Form.Label>
          <Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <Form.Label className="small text-muted">To</Form.Label>
          <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="flex-grow-1" style={{ minWidth: 200 }}>
          <Form.Label className="small text-muted">Tenant</Form.Label>
          <Form.Control value={tenantSearch} onChange={(e) => setTenantSearch(e.target.value)} placeholder="Search" />
        </div>
        <Button size="sm" onClick={() => load()}>
          Apply
        </Button>
      </div>

      {loading && !rows.length ? (
        <LoadingSkeleton rows={6} cols={4} />
      ) : (
        <DataTableWrapper columns={columns} data={rows} loading={false} keyField="id" onRowClick={(r) => setDrawer(r)} showSearch={false} />
      )}

      <DrawerPanel isOpen={Boolean(drawer)} onClose={() => setDrawer(null)} title="Invoice detail" width={480}>
        {drawer && (
          <div className={styles.drawerSection}>
            <div className="fw-bold mb-2">{drawer.invoiceNumber}</div>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {(drawer.lineItems || []).map((li, i) => (
                  <tr key={i}>
                    <td>{li.desc}</td>
                    <td>{li.qty}</td>
                    <td>${li.totalUsd}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex justify-content-between">
              <span>Tax</span>
              <span>${drawer.taxUsd}</span>
            </div>
            <div className="d-flex justify-content-between fw-bold">
              <span>Total</span>
              <span>${drawer.totalUsd}</span>
            </div>
            <h6 className="mt-3">Payment history</h6>
            <ul className="small">
              {(drawer.paymentHistory || []).map((p, i) => (
                <li key={i}>
                  {new Date(p.at).toLocaleString()} — {p.status} ({p.detail})
                </li>
              ))}
            </ul>
            <Form.Label>Payment notes</Form.Label>
            <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            <div className="mt-2">
              <Form.Label className="small">Paid date</Form.Label>
              <DatePicker selected={paidDate} onChange={(d) => d && setPaidDate(d)} className="form-control form-control-sm" />
            </div>
            <Button
              className="mt-2"
              size="sm"
              onClick={async () => {
                try {
                  await markInvoicePaidAPI(drawer.id, paidDate.toISOString());
                  toast.success('Marked paid');
                  setDrawer(null);
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Mark paid
            </Button>
          </div>
        )}
      </DrawerPanel>
    </div>
  );
}
